// services/DepartmentApiService.js
import {
    APIDepartmentList,
    APIDepartmentStore,
    APIDepartmentUpdate,
    APIDepartmentDelete,
    APIDepartmentNames
} from '../services/APIs';
import { api } from './authService';
import {
    connectionErrorMsg,
    Message_400,
    Message_401,
    Message_403,
    Message_404,
    Message_405,
    Message_409,
    Message_422,
    Message_500,
    Message_503
} from './errorMsgs';

class DepartmentApiService {
    constructor() {
        this.showToast = null;
    }

    /**
     * Set the toast function from useToast hook
     */
    setToastFunction(toastFunction) {
        this.showToast = toastFunction;
    }

    /**
     * Handle API errors consistently based on backend controller structure
     */
    handleError(error, defaultMessage = 'Operation failed') {
        let message = defaultMessage;
        let statusCode = null;
        let errors = null;
        let isIntegrityConstraint = false;

        if (error.response) {
            statusCode = error.response.status;
            const responseData = error.response.data || {};
            
            // Use backend message if available, otherwise fallback to our defaults
            message = responseData.message || defaultMessage;
            errors = responseData.errors || null;

            // Check for database integrity constraint errors
            const backendError = responseData.error || '';
            isIntegrityConstraint = backendError.includes('Integrity constraint violation') || 
                                   backendError.includes('foreign key constraint fails') ||
                                   backendError.includes('1451');

            // Handle specific backend response patterns
            switch (statusCode) {
                case 400:
                    // Handle business logic errors (like "Cannot delete branch with sub-branches")
                    if (responseData.message && responseData.message.includes('Cannot delete')) {
                        message = responseData.message;
                    } else {
                        message = message || Message_400;
                    }
                    break;
                case 401:
                    message = message || Message_401;
                    break;
                case 403:
                    message = message || Message_403;
                    break;
                case 404:
                    message = message || Message_404;
                    break;
                case 405:
                    message = message || Message_405;
                    break;
                case 409:
                    message = message || Message_409;
                    break;
                case 422:
                    // Backend returns validation errors with 'errors' object
                    if (errors) {
                        // Format validation errors into a readable string
                        const errorMessages = Object.values(errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        message = `${message || Message_422}:\n${errorMessages}`;
                    } else {
                        message = message || Message_422;
                    }
                    break;
                case 500:
                    // Handle database integrity constraint errors
                    if (isIntegrityConstraint) {
                        message = 'Cannot delete this department because it has related employees assigned. ' +
                                 'Please reassign or remove the employees first before deleting this department.';
                    } 
                    // Handle other database errors
                    else if (backendError && backendError.includes('Database Error')) {
                        message = `Database Error: ${message}`;
                    } else {
                        message = message || Message_500;
                    }
                    break;
                case 503:
                    message = message || Message_503;
                    break;
                default:
                    message = message || `Server error (${statusCode})`;
            }
        } else if (error.request) {
            message = connectionErrorMsg;
        } else {
            message = error.message || defaultMessage;
        }

        if (this.showToast) {
            this.showToast("Error", message, "error");
        } else {
            console.warn('Toast function not set. Message:', message);
        }

        return {
            success: false,
            message,
            statusCode,
            errors,
            isIntegrityConstraint,
            error: error.response?.data || error
        };
    }

    /**
     * Get department names (for dropdowns)
     */
    async listNames(branchId = null) {
        try {
            const params = {};
            if (branchId) { params.branch_id = branchId; }
            const response = await api.get(APIDepartmentNames, { params });
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    message: responseData.message || 'Departments fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch departments'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch departments');
        }
    }

    /**
     * Get department list with full details (for table)
     */
    async list(branchId = null) {
        try {
            const params = {};
            if (branchId) { params.branch_id = branchId; }
            const response = await api.get(APIDepartmentList, { params });
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                const departments = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: departments,
                    message: responseData.message || 'Departments fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch departments'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch departments');
        }
    }

    /**
     * Create department
     */
    async create(departmentData) {
        try {
            const response = await api.post(APIDepartmentStore, departmentData);
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true, 
                    data: responseData.data,
                    message: responseData.message || 'Department created successfully'
                };
            } else {
                // Backend returned error status (validation or duplicate)
                if (response.status === 422 && responseData.errors) {
                    // Format validation errors for display
                    const errorMessages = Object.values(responseData.errors)
                        .flat()
                        .map(err => `• ${err}`)
                        .join('\n');
                    return {
                        success: false,
                        message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                        errors: responseData.errors
                    };
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: responseData.message || 'Failed to create department'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to create department');
        }
    }

    /**
     * Update department
     */
    async update(departmentData) {
        try {
            const response = await api.post(APIDepartmentUpdate, departmentData);
            const responseData = response.data;
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Department updated successfully'
                };
            } else {
                // Handle different error cases
                if (response.status === 404) {
                    return {
                        success: false,
                        message: responseData.message || 'Department not found'
                    };
                } else if (response.status === 422) {
                    if (responseData.errors) {
                        const errorMessages = Object.values(responseData.errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        return {
                            success: false,
                            data: responseData.data,
                            message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                            errors: responseData.errors
                        };
                    } else if (responseData.message === 'Given Record Data Already Exists') {
                        return {
                            success: false,
                            message: responseData.message,
                            errors: responseData.errors
                        };
                    }
                }
                return {
                    success: false,
                    message: responseData.message || 'Failed to update department'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to update department');
        }
    }

    /**
     * Delete department
     */
    async delete(formData) {
        try {
            const response = await api.post(APIDepartmentDelete, formData);
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Department deleted successfully'
                };
            } else {
                // Handle business logic errors (400 status with custom message)
                if (response.status === 400 && responseData.message) {
                    return {
                        success: false,
                        message: responseData.message,
                        statusCode: 400
                    };
                }
                if (response.status === 404) {
                    return {
                        success: false,
                        message: responseData.message || 'Department not found'
                    };
                }
                return {
                    success: false,
                    message: responseData.message || 'Failed to delete department'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete department');
        }
    }
}

export const departmentApiService = new DepartmentApiService();